import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { setTermsVersionCallable } from '@/lib/callables';
import { subscribeTermsConfig, type TermsConfigResult } from '@/lib/firestoreTermsConfig';

const REASON_MIN = 3;
const VERSION_MAX = 40;

/**
 * Versão vigente dos Termos de Uso (#679 9.13) — `/appConfig/terms`.
 *
 * É a versão que vai junto de cada aceite: o do Checkout da venda assistida e o
 * `acceptTerms` do gate de primeiro login (F4). Sem documento, o aceite da
 * venda é gravado sem versão — por isso o estado ausente aparece como alerta.
 *
 * Origem: #700 — commit 5fc292f3b, 2026-09-23 19:26 +0200 (#679, versão dos
 * Termos ganha escritor).
 *
 * Contrato: assina `subscribeTermsConfig` (`lib/firestoreTermsConfig.ts`).
 * Salvar exige versão não vazia, até `VERSION_MAX` caracteres, diferente da
 * atual, e motivo com pelo menos `REASON_MIN`; grava pela callable
 * `setTermsVersion` (revalida e audita `TERMS_VERSION_UPDATED` em `/audit`).
 * Falha → toast e `console.error`.
 *
 * Usado por: `routes/AppVersionGatePage.tsx` (card abaixo da tabela de
 * versões, `/settings/app-versions`).
 *
 * Dados: `appConfig/terms` (mesma coleção do kill switch; leitura aberta,
 * escrita só pela callable). A versão gravada aqui é lida no Checkout da venda
 * e no `acceptTerms`.
 *
 * Issues: #679 (9.13, F4), #700.
 */
export function TermsVersionContainer(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<TermsConfigResult>({ kind: 'LOADING' });
  const [version, setVersion] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => subscribeTermsConfig(setState), []);

  const current = state.kind === 'READY' ? (state.config?.version ?? null) : null;
  const trimmedVersion = version.trim();
  const versionInvalid = trimmedVersion.length === 0 || trimmedVersion.length > VERSION_MAX;
  const versionUnchanged = !versionInvalid && trimmedVersion === current;
  const reasonTooShort = reason.trim().length < REASON_MIN;
  const canSubmit =
    state.kind === 'READY' && !versionInvalid && !versionUnchanged && !reasonTooShort && !saving;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await setTermsVersionCallable({ version: trimmedVersion, reason: reason.trim() });
      toast.success(t('admin.termsVersion.saved', { version: trimmedVersion }));
      setVersion('');
      setReason('');
    } catch (err) {
      console.error('admin/termsVersion: falha ao salvar', err);
      toast.error(t('admin.termsVersion.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Container
      header={
        <Header variant="h2" description={t('admin.termsVersion.subtitle')}>
          {t('admin.termsVersion.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{t('admin.termsVersion.loadFailed')}</Alert>}
      {state.kind === 'READY' && (
        <SpaceBetween size="m">
          {state.config === null ? (
            <Alert type="warning" header={t('admin.termsVersion.missingTitle')}>
              {t('admin.termsVersion.missingBody')}
            </Alert>
          ) : (
            <KeyValuePairs
              columns={2}
              items={[
                { label: t('admin.termsVersion.current'), value: state.config.version },
                {
                  label: t('admin.termsVersion.updatedAt'),
                  value:
                    state.config.updatedAt == null
                      ? '—'
                      : new Date(state.config.updatedAt).toLocaleString(i18n.language),
                },
              ]}
            />
          )}
          <FormField
            label={t('admin.termsVersion.versionLabel')}
            description={t('admin.termsVersion.versionHint')}
            errorText={versionUnchanged ? t('admin.termsVersion.errUnchanged') : undefined}
          >
            <Input
              value={version}
              placeholder="2026-04-29"
              onChange={({ detail }) => {
                setVersion(detail.value);
              }}
            />
          </FormField>
          <FormField
            label={t('admin.termsVersion.reasonLabel')}
            errorText={
              reasonTooShort && reason.length > 0 ? t('admin.termsVersion.errReason') : undefined
            }
          >
            <Textarea
              value={reason}
              onChange={({ detail }) => {
                setReason(detail.value);
              }}
            />
          </FormField>
          <Box float="right">
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={saving}
              onClick={() => {
                void submit();
              }}
            >
              {t('admin.termsVersion.save')}
            </Button>
          </Box>
        </SpaceBetween>
      )}
    </Container>
  );
}
